/**
 * Geometry for drawing a shark's spots as a star chart.
 *
 * Spots come in image pixel coordinates. A chart crops to the shark, links nearby spots into
 * "constellations" (a Euclidean minimum spanning tree with its long edges cut, so the pattern
 * falls into a few asterisms instead of one sprawling tree) and sizes each star by the spot's
 * radius, the way an atlas sizes stars by magnitude.
 */
import type { Constellation, View } from "@/api/types";

/**
 * Where each view's head points once turned (radians, x right, y down): a left flank faces
 * left and a right flank right, both back-up as photographed side-on, and a top-down photo
 * head-up. Turning, never mirroring: a mirrored flank would show a pattern the shark lacks.
 */
const FACING: Record<View, number> = { left: Math.PI, right: 0, top: -Math.PI / 2 };
const MIN_TURN = Math.PI / 180;

/** A constellation turned into its view's facing, with what was done to its photo. */
export interface Oriented extends Constellation {
  /** Rotation applied about the source photo's centre, radians. */
  turn: number;
  /** Source photo size, and where its turned corner box starts. */
  source: { width: number; height: number; x: number; y: number };
}

function isOriented(c: Constellation): c is Oriented {
  return "turn" in c;
}

function wrapAngle(a: number): number {
  return Math.atan2(Math.sin(a), Math.cos(a));
}

/**
 * The turn that brings a constellation's head to its view's facing. A flank is never turned
 * past a quarter: on LILA's labelled flanks 14% read their head within 45 degrees of the
 * wrong way, far more than are photographed back-down, so such a flank is levelled with its
 * back kept where it was. A top-down photo can lie at any angle, so it turns fully.
 */
function turnFor(c: Constellation): number {
  if (!c.view || c.heading === null) return 0;
  let turn = wrapAngle(FACING[c.view] - c.heading);
  if (c.view !== "top" && Math.abs(turn) > Math.PI / 2) turn = wrapAngle(turn + Math.PI);
  return Math.abs(turn) < MIN_TURN ? 0 : turn;
}

/**
 * Turn a constellation so its head points the way its view faces (see FACING and turnFor),
 * on a canvas just large enough for the turned photo. Unknown views or headings, and turns
 * under a degree, are left as photographed. Idempotent.
 */
export function orient(c: Constellation): Oriented {
  if (isOriented(c)) return c;
  const turn = turnFor(c);
  const cos = Math.cos(turn);
  const sin = Math.sin(turn);
  const cx = c.width / 2;
  const cy = c.height / 2;
  const rot = (x: number, y: number): [number, number] => [
    (x - cx) * cos - (y - cy) * sin,
    (x - cx) * sin + (y - cy) * cos,
  ];
  const corners = [rot(0, 0), rot(c.width, 0), rot(0, c.height), rot(c.width, c.height)];
  const x0 = Math.min(...corners.map((p) => p[0]));
  const y0 = Math.min(...corners.map((p) => p[1]));
  const move = (x: number, y: number): [number, number] => {
    const [rx, ry] = rot(x, y);
    return [rx - x0, ry - y0];
  };
  return {
    ...c,
    width: Math.max(...corners.map((p) => p[0])) - x0,
    height: Math.max(...corners.map((p) => p[1])) - y0,
    spots: c.spots.map(([x, y, r]) => [...move(x, y), r]),
    body_polygon: c.body_polygon.map(([x, y]) => move(x, y)),
    fin: c.fin ? move(c.fin[0], c.fin[1]) : null,
    heading: c.heading === null ? null : c.heading + turn,
    turn,
    source: { width: c.width, height: c.height, x: x0, y: y0 },
  };
}

/** SVG transform that draws the source photo where an oriented constellation's points are. */
export function photoTransform(o: Oriented): string {
  const { width, height, x, y } = o.source;
  const deg = (o.turn * 180) / Math.PI;
  return `translate(${-x} ${-y}) rotate(${deg}) translate(${-width / 2} ${-height / 2})`;
}

export interface Point {
  x: number;
  y: number;
}

export interface Bounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

export type Edge = [number, number];

export function spotPoints(c: Constellation): Point[] {
  return c.spots.map(([x, y]) => ({ x, y }));
}

export function boundsOf(points: readonly Point[]): Bounds | null {
  if (points.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

/** The region worth showing: body outline and spots together, else the whole image. */
export function constellationBounds(c: Constellation): Bounds {
  const outline = c.body_polygon.map(([x, y]) => ({ x, y }));
  const bounds = boundsOf([...outline, ...spotPoints(c)]);
  if (!bounds || bounds.width === 0 || bounds.height === 0) {
    return { x: 0, y: 0, width: c.width, height: c.height };
  }
  return bounds;
}

/** Grow bounds by `fraction` of the larger side on every edge. */
export function padBounds(b: Bounds, fraction: number): Bounds {
  const pad = Math.max(b.width, b.height) * fraction;
  return { x: b.x - pad, y: b.y - pad, width: b.width + 2 * pad, height: b.height + 2 * pad };
}

/**
 * Expand the short side of `b` (about its centre) so width / height == aspect. If the result
 * fits inside `frame`, it is shifted to lie within it rather than spilling past the image edge.
 */
export function fitToAspect(b: Bounds, aspect: number, frame?: Bounds): Bounds {
  if (!(aspect > 0)) throw new Error(`Aspect ratio must be positive, got ${aspect}`);
  let { width, height } = b;
  if (width / height > aspect) height = width / aspect;
  else width = height * aspect;
  let x = b.x + b.width / 2 - width / 2;
  let y = b.y + b.height / 2 - height / 2;
  if (frame) {
    if (width <= frame.width) x = clamp(x, frame.x, frame.x + frame.width - width);
    if (height <= frame.height) y = clamp(y, frame.y, frame.y + frame.height - height);
  }
  return { x, y, width, height };
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(Math.max(v, lo), hi);
}

export function distance(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/** Prim's algorithm on the complete graph, O(n²) — fine for the ≤ a few hundred spots we get. */
export function minimumSpanningTree(points: readonly Point[]): Edge[] {
  const n = points.length;
  if (n < 2) return [];
  const inTree: boolean[] = Array.from({ length: n }, () => false);
  const best: number[] = Array.from({ length: n }, () => Infinity);
  const parent: number[] = Array.from({ length: n }, () => -1);
  const edges: Edge[] = [];
  best[0] = 0;
  for (let step = 0; step < n; step++) {
    let u = -1;
    for (let i = 0; i < n; i++) {
      if (!inTree[i] && (u === -1 || best[i]! < best[u]!)) u = i;
    }
    inTree[u] = true;
    if (parent[u]! >= 0) edges.push([parent[u]!, u]);
    const pu = points[u]!;
    for (let v = 0; v < n; v++) {
      if (inTree[v]) continue;
      const d = distance(pu, points[v]!);
      if (d < best[v]!) {
        best[v] = d;
        parent[v] = u;
      }
    }
  }
  return edges;
}

export function median(values: readonly number[]): number {
  if (values.length === 0) return 0;
  const sorted = values.toSorted((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2;
}

/**
 * Constellation lines: MST edges no longer than `maxEdgeFactor` × the median MST edge.
 * Cutting the long edges splits the tree into several asterisms.
 */
export function constellationEdges(points: readonly Point[], maxEdgeFactor = 1.8): Edge[] {
  const tree = minimumSpanningTree(points);
  if (tree.length === 0) return [];
  const lengths = tree.map(([a, b]) => distance(points[a]!, points[b]!));
  const limit = median(lengths) * maxEdgeFactor;
  return tree.filter((_, i) => lengths[i]! <= limit);
}

/**
 * Magnitude class 1 (brightest) … 5 (faintest) from spot radius by rank, so the chart has a
 * few bright stars and many faint ones regardless of the photo's resolution.
 */
export function magnitudes(radii: readonly number[]): number[] {
  if (radii.length === 0) return [];
  // Hand-marked spots often carry one nominal radius: rank would invent brightness, so don't.
  if (radii.every((r) => r === radii[0])) return radii.map(() => 3);
  const order = radii.map((r, i) => [r, i] as const).toSorted((a, b) => b[0] - a[0]);
  const classes: number[] = Array.from({ length: radii.length }, () => 5);
  const cuts = [0.04, 0.14, 0.34, 0.62]; // cumulative share of stars brighter than each class
  order.forEach(([, index], rank) => {
    const q = radii.length === 1 ? 0 : rank / radii.length;
    let magnitude = 1;
    while (magnitude <= cuts.length && q >= cuts[magnitude - 1]!) magnitude++;
    classes[index] = magnitude;
  });
  return classes;
}

// ------------------------------------------------------------------ randomness

/** Deterministic 32-bit PRNG (mulberry32); same seed, same sky. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** FNV-1a hash of a string, for seeding. */
export function hashString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}
